"use client";

import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { createPath } from "@/service/routing/routing";
import {
  ArrowUpRightIcon,
  ChevronRightIcon,
  DotIcon,
  EarthIcon,
  FlagIcon,
  type LucideIcon,
  MapIcon,
} from "lucide-react";
import { useContext } from "react";
import { CardSlider } from "@/components/common/card-slider";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { PageSection } from "@/components/common/page-section/page-section";
import { cn } from "@/lib/utils";

export const HomeNewsFeed = ({
  newsFeed,
  moreNewsCount,
}: {
  newsFeed: NewsFeedItem[];
  moreNewsCount: number;
}) => {
  const { logView } = useContext(ResourceContext);
  if (newsFeed.length === 0) return null;
  return (
    <PageSection
      heading={
        <>
          Przegląd newsów <br />
          <div className={"text-sm font-normal text-zinc-600"}>
            Przetłumaczone i zredagowane przy wsparciu AI
          </div>
        </>
      }
      headingAdditions={
        <div className={"flex items-center gap-3 text-sm"}>
          <span className={"text-muted-foreground"}>
            W tym tygodniu{" "}
            <span className={"text-foreground font-semibold"}>
              +{moreNewsCount} newsów
            </span>
          </span>
          <Link
            href={createPath("news_feed.index")}
            onClick={() =>
              logEvent("home_news_feed_see_more", logView, { moreNewsCount })
            }
            className={
              "text-remedium-blue flex items-center gap-1 text-sm font-semibold transition-all"
            }
          >
            Zobacz więcej <ChevronRightIcon size={16} />
          </Link>
        </div>
      }
    >
      <CardSlider
        centerArrows
        className={"@container my-4"}
        items={[
          ...newsFeed.map((item) => <NewsFeedCard key={item.id} item={item} />),
          <NewsFeedMoreCard key={"more"} moreNewsCount={moreNewsCount} />,
        ]}
        basis="basis-[328px] md:basis-[400px] ml-4"
      />
    </PageSection>
  );
};

const formatDayMonth = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
};

const newsFeedCountryIcons: Record<string, LucideIcon> = {
  polska: FlagIcon,
  świat: EarthIcon,
  europa: MapIcon,
};

const getNewsFeedCountryIcon = (originCountry: string) =>
  newsFeedCountryIcons[originCountry?.trim().toLowerCase()] ?? null;

const NewsFeedCard = ({ item }: { item: NewsFeedItem }) => {
  const { logView } = useContext(ResourceContext);
  const displayDate = formatDayMonth(
    item.sourcePublishedAt ?? item.displayDate,
  );
  const CountryIcon = getNewsFeedCountryIcon(item.originCountry);

  return (
    <Link
      href={createPath("news_feed.index") + `?news-id=${item.id}`}
      className={
        "group flex h-full flex-col gap-4 rounded-2xl border bg-zinc-50 px-4 py-3 transition-all hover:border-neutral-300"
      }
      onClick={() => {
        logEvent("news_feed_item_click", logView, { news_feed_item: item });
      }}
    >
      <div
        className={
          "-mx-4 -mt-3 flex items-center justify-between gap-2 rounded-t-2xl px-4 py-2"
        }
      >
        {item.sourceName && (
          <span
            className={
              "text-muted-foreground flex min-w-0 items-center gap-1 text-xs font-medium"
            }
          >
            {item.iconLink ? (
              <img
                src={item.iconLink}
                alt={""}
                className={"size-6 shrink-0 rounded-full object-cover"}
              />
            ) : (
              CountryIcon && <CountryIcon size={12} className={"shrink-0"} />
            )}
            <span className={"truncate"}>{item.sourceName}</span>
          </span>
        )}

        <div className={"flex items-center gap-0.5"}>
          <Badge
            className={`rounded-full bg-white px-2 py-0.5 text-sm font-semibold text-zinc-800`}
          >
            {item.tag}
          </Badge>

          {item.tag && displayDate && (
            <DotIcon size={16} className={"text-muted-foreground"} />
          )}
          <span className={`text-muted-foreground text-xs font-medium`}>
            {displayDate}
          </span>
        </div>
      </div>

      <div className={"flex flex-1 flex-col gap-3"}>
        <h3
          className={
            "group-hover:text-remedium-blue text-base font-normal [&_span]:font-semibold"
          }
          dangerouslySetInnerHTML={{ __html: item.title }}
        />

        {item.aiTags && item.aiTags.length > 0 && (
          <div className={"mt-auto flex items-center gap-2 overflow-hidden"}>
            {item.aiTags.slice(0, 2).map((aiTag, aiTagIndex, visibleTags) => (
              <span
                key={aiTag}
                className={cn(
                  "rounded-full border bg-white px-2.5 py-0.5 text-xs font-medium whitespace-nowrap text-black",
                  aiTagIndex === visibleTags.length - 1
                    ? "truncate"
                    : "shrink-0",
                )}
              >
                {aiTag}
              </span>
            ))}
            {item.aiTags.length > 2 && (
              <span
                className={
                  "shrink-0 rounded-full border bg-white px-2.5 py-0.5 text-xs font-medium whitespace-nowrap text-black"
                }
              >
                +{item.aiTags.length - 2}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

const NewsFeedMoreCard = ({ moreNewsCount }: { moreNewsCount: number }) => {
  const { logView } = useContext(ResourceContext);
  return (
    <Link
      href={createPath("news_feed.index")}
      onClick={() =>
        logEvent("home_news_feed_more_card_click", logView, { moreNewsCount })
      }
      className={
        "group flex h-full flex-col justify-between gap-4 rounded-2xl border p-5 transition-all hover:border-neutral-300"
      }
    >
      <div className={"flex flex-col gap-1"}>
        <span className={"text-remedium-blue text-3xl font-bold"}>
          {moreNewsCount}+
        </span>
        <span className={"text-base font-[550]"}>newsów z ostatnich 7 dni</span>
      </div>
      <div className={"flex items-end justify-between gap-2"}>
        <span className={"text-muted-foreground text-sm"}>
          Zobacz pełen przegląd newsów
        </span>
        <span
          className={
            "bg-remedium-blue flex size-8 shrink-0 items-center justify-center rounded-lg text-white transition-all group-hover:scale-105"
          }
        >
          <ArrowUpRightIcon size={16} />
        </span>
      </div>
    </Link>
  );
};
